<template>
    <div style="width: 100%">
        <v-overlay v-model="loading" opacity="0.8">
            <FullscreenLoader />
        </v-overlay>
        <div v-if="!loading" style="width: 100%">
            <v-row style="width: 100%">
                <v-col cols="12">
                    <v-breadcrumbs
                        id="calc-bread"
                        class="ma-0 pa-0"
                        :items="getbreadcrumbs"
                    >
                        <template v-slot:divider>
                            <span class="text-body-medium text-disabled"
                                >/</span
                            >
                        </template>
                        <template v-slot:item="{ item }">
                            <v-breadcrumbs-item
                                exact
                                :to="item.href"
                                class="text-typography2 text-body-large"
                                >{{
                                    item.title || item.text
                                }}</v-breadcrumbs-item
                            >
                        </template>
                    </v-breadcrumbs>
                </v-col>
            </v-row>
            <v-row style="width: 100%">
                <v-col cols="12">
                    <CalcInfo :calc="calc" :libraryVersion="version" />
                </v-col>
            </v-row>
            <v-row style="width: 100%">
                <v-col sm="12" lg="6">
                    <CalcInputs
                        :calc="calc"
                        :loading="loadingResult"
                        :maxHeightNoButtons="maxHeightNoButtons"
                        :maxHeightButtons="maxHeightButtons"
                        :noButtonsPadding="noButtonsPadding"
                        @submit="submit"
                    />
                </v-col>
                <v-col sm="12" lg="6">
                    <CalcOutputs
                        :record="selectedRecord"
                        :calc="calc"
                        :loading="loadingResult"
                        :maxHeightNoButtons="maxHeightNoButtons"
                        :maxHeightButtons="maxHeightButtons"
                        :noButtonsPadding="noButtonsPadding"
                    />
                </v-col>
            </v-row>
            <v-row style="width: 100%">
                <v-col cols="12">
                    <CalcRecords
                        :records="calcRecords"
                        :loading="loadingResult"
                        :selectedRecordIndex="selectedRecordIndex"
                        @restore="restore"
                    />
                </v-col>
            </v-row>
        </div>
    </div>
</template>
<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

import CalcInfo from './CalcInfoCard.vue'
import CalcInputs from './CalcInputs.vue'
import CalcOutputs from './CalcOutputs.vue'
import CalcRecords from './CalcRecords.vue'
import helpers from '../../../helpers'
import FullscreenLoader from '../../shared/FullscreenLoader.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { proxy } = getCurrentInstance()

const loading = ref(true)
const loadingResult = ref(false)
const calcId = ref(0)
const persistentCalcId = ref('')
const error = ref(false)
const version = ref({})
const calc = ref({ calcId: 0 })
const hasFileInputs = ref(false)
const calcRecords = ref([])
const selectedRecord = ref({})
const selectedRecordIndex = ref(0)
const maxHeightNoButtons = '42rem'
const maxHeightButtons = '38rem'
const noButtonsPadding = 'pb-5'

const getbreadcrumbs = computed(() => {
    let items = []
    if (!calc.value.api) return items
    let apiarray = calc.value.api.split('/')
    for (var [index, value] of apiarray.entries()) {
        let link = `/library/${version.value.libraryId}?path=v${version.value.version}`
        if (index !== 0) {
            link += `-${apiarray.slice(1, index + 1).join('-')}`
        }
        items.push({ text: value, href: link })
    }
    items.pop()
    return items
})

function reset(pId = '', cId = 0) {
    loading.value = true
    loadingResult.value = false
    calcId.value = cId
    persistentCalcId.value = pId
    error.value = false
    version.value = {}
    calc.value = { calcId: 0 }
    calcRecords.value = []
    selectedRecord.value = {}
    selectedRecordIndex.value = 0
}

async function fullSetup() {
    let c
    if (route.params.calcId) {
        c = route.params.calcId
        calcId.value = c
        await pageSetup(setupWithCalcId, route.params.calcId, route.query)
    } else if (route.params.persistentId) {
        c = route.params.persistentId
        persistentCalcId.value = c
        await pageSetup(
            setupWithPersistentId,
            route.params.persistentId,
            route.query
        )
    }
    loading.value = false
    const queryKeys = Object.keys(route.query)
    if (
        queryKeys.length > 0 &&
        !(queryKeys.length === 1 && queryKeys.includes('posthog'))
    )
        await submit()
    if (route.query.posthog && proxy.$posthog) {
        proxy.$posthog.capture(route.query.posthog, { calc: c })
    }
}

async function pageSetup(calcfn, id, query, load) {
    if (load) loading.value = true
    let c
    try {
        c = await calcfn(id, query)
    } catch (err) {
        error.value = true
        loading.value = false
        return
    }
    if (c.calcId === undefined) {
        store.commit('SET_ALERT_ERRORS', [
            `The calc you're trying to use has either moved or been deleted. <a href="/docs/explanation/404.html" target="_blank">Refer to the documentation site for next steps.</a>`,
        ])
        error.value = true
        loading.value = false
        return
    }
    version.value = await store.dispatch(
        'getLibraryVersion',
        c.libraryVersionId
    )
    calc.value = c
    calc.value.inputs.forEach((input) => {
        if (input.isFile) hasFileInputs.value = true
    })
}

function restore(record) {
    selectedRecord.value = record
    selectedRecordIndex.value = calcRecords.value.findIndex(
        (r) => r.calcRecordId === record.calcRecordId
    )
    let inputKeys = Object.keys(record.body)
    inputKeys.forEach((key) => {
        const index = calc.value.inputs.findIndex((i) => i.name === key)
        if (index !== -1) calc.value.inputs[index].value = record.body[key]
    })
    if (calc.value.inputGroups) {
        calc.value.inputGroups.forEach((ig) => {
            ig.inputs.forEach((i) => {
                if (inputKeys.includes(i.name)) i.value = record.body[i.name]
            })
        })
    }
    setUrlQueryParams()
}

async function setupWithCalcId(id, query) {
    store.dispatch('warmLibraryVersionByCalcId', id)
    return await store.dispatch('getCalc', {
        id,
        query,
        includeInputGroups: true,
    })
}

async function setupWithPersistentId(id, query) {
    const v = {
        major: route.params.major !== undefined ? route.params.major : -1,
        minor: route.params.minor !== undefined ? route.params.minor : -1,
        patch: route.params.patch !== undefined ? route.params.patch : -1,
    }
    return await store.dispatch('getCalcByPersistentId', {
        id,
        query,
        version: v,
        includeInputGroups: true,
    })
}

function genBody() {
    let body = {}
    if (calc.value.inputs) {
        calc.value.inputs.forEach((input) => {
            body[input.name] = helpers.valueToSend(input)
        })
    }
    return body
}

function setUrlQueryParams() {
    if (hasFileInputs.value) return
    var queryobj = {}
    if (calc.value.inputs) {
        calc.value.inputs.forEach((i) => {
            queryobj[i.name] = i.value.toString()
        })
    }
    if (JSON.stringify(queryobj) !== JSON.stringify(route.query)) {
        router.replace({ name: route.name, query: queryobj })
    }
}

function getFileInputs() {
    if (!hasFileInputs.value) return []
    return calc.value.inputs
        .map((input) =>
            input.isFile ? { name: input.name, value: input.value } : null
        )
        .filter((v) => v !== null)
}

async function submit() {
    loadingResult.value = true
    let body = genBody()
    let fileInputs = getFileInputs()
    let calcRecord = await store.dispatch('calculate', {
        body,
        calcId: calc.value.calcId,
        hasFileInputs: hasFileInputs.value,
    })
    if (calcRecord) {
        if (hasFileInputs.value) {
            fileInputs.forEach((fi) => {
                calcRecord.body[fi.name] = fi.value
            })
        }
        let records = calcRecords.value
        if (Array.isArray(records) && records.length) {
            records.unshift(calcRecord)
        } else {
            records = [calcRecord]
        }
        calcRecords.value = records
        selectedRecord.value = calcRecord
        selectedRecordIndex.value = 0
    }
    setUrlQueryParams()
    loadingResult.value = false
}

watch(route, async (to, from) => {
    if (to.name === 'calculation' || to.name.includes('persistentCalcId')) {
        if (
            to.name === 'calculation' &&
            parseInt(to.params.calcId) !== +calcId.value
        ) {
            loading.value = true
            reset('', calcId.value)
            await fullSetup()
        } else if (
            to.name.includes('persistentCalcId') &&
            to.params.persistentId !== persistentCalcId.value
        ) {
            loading.value = true
            reset(persistentCalcId.value)
            await fullSetup()
        }
    }
})

onMounted(async () => {
    await fullSetup()
})
</script>
